<template>
  <VForm
    v-if="localEvent && Object.keys(localEvent).length"
    id="form-event-creation-modal-details"
    ref="form"
    :initial-values="initialFormValues"
    @submit="submitEvent"
    @invalid-submit="doTrack(false)"
  >
    <!-- event title -->
    <div class="mb-4">
      <SInput
        id="event-name"
        v-model="localEvent.name"
        validation-rules="min:2|max:150|required"
        validation-field-name="name"
        data-cy="event-title"
        :placeholder="$t(`calendar.event_creation.details.input.placeholder.${isEditMode ? 'edit' : 'create'}`)"
        :label-text="$t(`calendar.event_creation.details.input.placeholder.${isEditMode ? 'edit' : 'create'}`)"
        hide-label
        size="lg"
      />
    </div>

    <!-- event is full day -->
    <SListItem
      :title="$t('calendar.event_creation.details.full_day.title')"
      :aria-pressed="localEvent.is_all_day ? 'true' : 'false'"
      data-cy="event-is-full-day"
      @click="localEvent.is_all_day = !localEvent.is_all_day"
    >
      <template #right-action>
        <SCheckbox
          v-model="localEvent.is_all_day"
          name="is_all_day"
          :label-text="$t('calendar.event_creation.details.full_day.title')"
          checkbox-type="switch"
        />
      </template>
    </SListItem>

    <!-- event begins_at & event ends_at -->
    <SDateRangePicker v-model="localEvent"
                      :enable-time-select="!localEvent.is_all_day"
                      class="mt-4 pb-4"
    />

    <SListItem
      :title="$t('calendar.event_creation.details.category.title')"
      :right-action-label="categoryLabel"
      data-cy="open-event-categories"
      right-action-icon="icon-chevron-right"
      @click="navigate('event-categories')"
    />

    <!-- event participants -->
    <SListItem
      class="mt-4"
      :right-action-label="inviteeLabel"
      data-cy="open-event-invitees"
      :title="$t('calendar.event_creation.details.participants.title')"
      right-action-icon="icon-chevron-right"
      @click="navigate('event-participants')"
    />

    <!-- disable notification for participants -->
    <SListItem
      :title="$t('calendar.event_details.delete.notify_participants')"
      :aria-pressed="localEvent.is_notifiable ? 'true' : 'false'"
      data-cy="notify-participants"
      class="mt-4"
      @click="localEvent.is_notifiable = !localEvent.is_notifiable"
    >
      <template #right-action>
        <SCheckbox
          v-model="localEvent.is_notifiable"
          name="is_notifiable"
          :label-text="$t('calendar.event_details.delete.notify_participants')"
          checkbox-type="switch"
        />
      </template>
    </SListItem>

    <!-- event description -->
    <STextarea
      id="event-description"
      v-model="localEvent.description"
      class="event-description-textarea mt-4"
      data-cy="event-description"
      :label-text="$t('calendar.event_creation.details.description.title')"
      :placeholder="$t('calendar.event_creation.details.description.placeholder')"
      rows="6"
    />

    <div class="text-right">
      <b-button class="pr-0 mt-2"
                variant="link"
                @click="showMarkdownModal = true"
      >
        {{ $t('global.modals.markdown.button') }}
      </b-button>
    </div>

    <!-- create event submit -->
    <SModalPortalTo>
      <b-form-group class="mb-0 w-100">
        <STooltip :title="$t('calendar.event_creation.wrapper.tooltip')"
                  :disabled="!isDisabled"
                  :trigger-element-disabled="isDisabled"
        >
          <template #tooltip-trigger="{ triggerAttrs }">
            <b-button
              v-btn-focus-restore-after-disable
              v-bind="triggerAttrs"
              form="form-event-creation-modal-details"
              type="submit"
              size="lg"
              block
              :class="isSubmitDisabled && 'no-spinner'"
              variant="primary"
              data-cy="submit-event"
              :disabled="isDisabled"
            >
              {{ submitLabel }}
            </b-button>
          </template>
        </STooltip>
      </b-form-group>
    </SModalPortalTo>

    <SModal
      v-model="showMarkdownModal"
      :header-title="$t('global.modals.markdown.title')"
      hide-footer
      size="lg"
      @hidden="showMarkdownModal = false"
    >
      <sdui-markdown-modal v-if="showMarkdownModal" @close="showMarkdownModal = false" />
    </SModal>
  </VForm>
</template>

<script>
import SDateRangePicker from 'src/components/base/datepicker/SDateRangePicker.vue'
import dateTimePicker from 'src/mixins/miscellaneous/dateTimePicker.js'
import { categories } from 'src/views/Channels/Calendar/calendarCategories.js'
import { Channel, User } from 'src/store/models/index.js'
import clyTracking from 'src/config/services/countlyTracking/clyTracking.js'
import SduiMarkdownModal from 'src/components/miscellaneous/ui/modals/SduiMarkdownModal.vue'
import SCheckbox from 'src/components/base/SCheckbox.vue'
import SModalPortalTo from 'src/components/base/SModal/SModalPortalTo.vue'
import STooltip from 'src/components/base/STooltip.vue'

export default {
  name: 'EventCreationModalDetails',
  components: { SModalPortalTo, SduiMarkdownModal, SDateRangePicker, SCheckbox, STooltip },
  mixins: [dateTimePicker],
  props: {
    event: {
      type: Object,
      default: () => ({}),
    },

    participants: {
      type: Array,
      default: () => [],
    },

    requestingApi: {
      type: Boolean,
      default: false,
    },

    isEditMode: {
      type: Boolean,
      default: false,
    },

    isSubmitDisabled: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    localEvent: null,
    categories,
    Channel: Channel,
    User: User,
    showMarkdownModal: false,
  }),

  computed: {
    isDisabled () {
      return this.requestingApi || (this.isSubmitDisabled && this.isEditMode)
    },

    inviteeLabel() {
      if (!this.userInvitees && !this.channelInvitees) return this.$t('calendar.event_creation.details.participants.label_only_me')
      if (!this.userInvitees && this.channelInvitees)
        return this.$t('calendar.event_creation.details.participants.label_only_groups', { count: this.channelInvitees })
      if (this.userInvitees && !this.channelInvitees)
        return this.$t('calendar.event_creation.details.participants.label_only_persons', { count: this.userInvitees })
      return `${this.$t('calendar.event_creation.details.participants.label_only_groups', { count: this.channelInvitees })}, ${this.$t('calendar.event_creation.details.participants.label_only_persons', { count: this.userInvitees })}`
    },

    categoryLabel() {
      return this.$t(this.categories[this.localEvent.category]?.value)
    },

    userInvitees() {
      return this.localEvent.users?.length
    },

    channelInvitees() {
      return this.localEvent.channels?.length
    },

    submitLabel() {
      return this.isEditMode
        ? this.$t('calendar.event_creation.wrapper.submit.title.edit')
        : this.$t('calendar.event_creation.wrapper.submit.title.create')
    },

    initialFormValues() {
      return {
        name: this.localEvent?.name,
        begins_at: this.localEvent?.begins_at,
        ends_at: this.localEvent?.ends_at,
      }
    },
  },

  emits: ['update', 'submit'],

  methods: {
    navigate(route) {
      this.updateLatest()
      this.$router.push({ name: this.$route.name, query: { ...this.$route.query, [route]: true } })
    },

    updateLatest() {
      this.$emit('update', this.localEvent)
    },

    doTrack(isFormValid = true) {
      const result = isFormValid ? 'SUCCESS' : 'FAILED'
      const failedReason = isFormValid ? null : 'validation'
      clyTracking.trackCalendar('calendarEvent', result, failedReason).end.submit({ isEditMode: this.isEditMode, trackGlobal: { feature: 'calendar' } })
    },

    async submitEvent() {
      this.doTrack()
      this.$emit('submit')
    },

    getEventsLength(oldBeginsAt, newEndsAt, newBeginsAt) {
      const beginsAtClone = this.$moment(oldBeginsAt)
      const endsAtClone = this.$moment(newEndsAt)
      const length = Math.abs(beginsAtClone.diff(endsAtClone, 'minutes'))

      const beginsAtIsBeginsOfDay = this.getDateTime(oldBeginsAt).time === '00:00:00'
      const endsAtIsEndOfDay = this.getDateTime(endsAtClone).time === '23:59:59'
      const fullDayEvent = beginsAtIsBeginsOfDay && endsAtIsEndOfDay

      const newBeginsAtStartOfDay = this.getDateTime(newBeginsAt).time === '00:00:00'

      if (!length || (fullDayEvent && !newBeginsAtStartOfDay)) return 45
      return length
    },
  },

  watch: {
    localEvent: {
      handler: function (event) {
        // Update event in wrapper component
        this.$emit('update', event)
      },

      deep: true,
    },

    'localEvent.begins_at': {
      handler: function (newBeginsAt, oldBeginsAt) {
        if (!newBeginsAt || !oldBeginsAt || this.localEvent.is_all_day || this.isSubmitDisabled) return
        const endsAt = this.localEvent.ends_at
        const eventLength = this.getEventsLength(oldBeginsAt, endsAt, newBeginsAt)
        const date = this.$moment(newBeginsAt).add(eventLength, 'minutes')
        this.localEvent.ends_at = this.getDateTime(date).dateWithTime
      },
    },
  },

  mounted() {
    this.localEvent = this.$clone(this.event)
  },
}
</script>

<style scoped lang="scss">
:deep(.headline-empty) {
  margin-top: 0 !important;
}

.event-description-textarea :deep(.s-textarea__label) {
  line-height: pxtorem(24);
  font-size: pxtorem(20);
  letter-spacing: -0.2px;
}
</style>
